import { Dialog, Transition } from "@headlessui/react";
import {
  Fragment,
  MutableRefObject,
  ReactNode,
  useEffect,
  useState,
} from "react";

type Props = {
  isOpen: boolean;
  title?: string;
  content: string | ReactNode;
  controls?: ReactNode;
  initialFocus?: MutableRefObject<HTMLElement | null>;
  closeOnOutsideClick?: boolean;
};

export const useModalState = ({ isOpen }: Pick<Props, "isOpen">) => {
  const [open, setOpen] = useState(isOpen);
  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  return [open, setOpen] as const;
};

export default function Modal({
  isOpen,
  title,
  content,
  controls,
  initialFocus,
  closeOnOutsideClick = true,
}: Props) {
  const [open, setOpen] = useModalState({ isOpen });
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={initialFocus}
        onClose={(value) => {
          if (closeOnOutsideClick) setOpen(value);
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      {title && (
                        <Dialog.Title
                          as="h3"
                          className="text-lg font-semibold leading-6 text-gray-900"
                        >
                          {title}
                        </Dialog.Title>
                      )}

                      <div className="mt-2">
                        {typeof content === "string" ? (
                          <p className="text-lg text-black">{content}</p>
                        ) : (
                          <div>{content}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {controls && (
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    {controls}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
